// VC4U Consulting Website - Mock Data

export const companyInfo = {
  name: "VC4U",
  fullName: "Vyuham Consulting for You",
  tagline: "Consult, Code, Collaborate",
  email: "hello@vc4u.co.in",
  description: "Senior-led IT consulting firm specializing in Automotive Software, Mobile Platforms, and AI-enabled Engineering Services."
};

export const navLinks = [
  { label: "About", href: "#about" },
  { label: "Services", href: "#services" },
  { label: "Solutions", href: "/solutions", isRoute: true },
  { label: "Industries", href: "#industries" },
  { label: "Frameworks", href: "#frameworks" },
  { label: "Blogs", href: "/blogs", isRoute: true },
  { label: "Why Us", href: "#why-us" },
  { label: "Contact", href: "#contact" }
];

export const heroContent = {
  headline: "Engineering Excellence for Tomorrow's Mobility",
  subheadline: "Expert consulting that transforms automotive software, mobile platforms, and AI initiatives into measurable business outcomes.",
  cta: "Connect with Our Team",
  secondaryCta: "Explore Services"
};

export const valuePillars = [
  {
    title: "Expert-Led Delivery",
    description: "Every engagement led by consultants with 15+ years hands-on experience in OEM and Tier-1 environments."
  },
  {
    title: "Framework-Driven",
    description: "Proven methodologies aligned with SAFe, ASPICE, and industry-standard SDLC practices."
  },
  {
    title: "OEM-Ready",
    description: "Audit-ready processes, compliance matrices, and enterprise-grade documentation."
  },
  {
    title: "AI-Accelerated",
    description: "Intelligent automation for code review, testing, and requirements analysis."
  },
  {
    title: "Outcome-Focused",
    description: "Measurable KPIs, delivery health dashboards, and transparent governance."
  }
];

export const aboutContent = {
  title: "About VC4U",
  subtitle: "Engineering-First Consulting Culture",
  paragraphs: [
    "VC4U (Vyuham Consulting for You) is a senior-led IT software consulting firm built on a foundation of deep domain expertise in automotive software, mobile platforms, and AI-enabled engineering.",
    "Our team comprises seasoned professionals with 15+ years of hands-on experience in delivery leadership, system architecture, process engineering, and program management across global OEMs and Tier-1 suppliers.",
    "We don't deliver slideware. We deliver working solutions, robust processes, and teams that execute with precision."
  ],
  stats: [
    { value: "15+", label: "Years Average Experience" },
    { value: "100+", label: "Enterprise Projects" },
    { value: "50+", label: "OEM/Tier-1 Engagements" },
    { value: "98%", label: "Client Retention" }
  ]
};

export const services = [
  {
    id: "program-management",
    title: "Program & Project Management",
    description: "Large-scale program governance, SAFe implementation, and multi-vendor coordination for complex automotive initiatives.",
    features: [
      "SAFe / Agile at Scale Implementation",
      "PI Planning & Dependency Management",
      "Risk & Delivery Health Dashboards",
      "Multi-Vendor Coordination",
      "OEM-Style Milestone Tracking"
    ]
  },
  {
    id: "process-compliance",
    title: "Process & Compliance Consulting",
    description: "ASPICE assessment, SDLC establishment, and audit-ready process frameworks for automotive excellence.",
    features: [
      "Automotive SPICE (ASPICE) L1–L3",
      "SDLC Process Establishment",
      "Requirements → Design → Verification Traceability",
      "Quality Metrics, CTQs, KPIs",
      "Audit Readiness & Gap Analysis"
    ]
  },
  {
    id: "automotive-software",
    title: "Automotive Software Engineering",
    description: "End-to-end automotive software development from AAOS customization to infotainment platforms and system optimization.",
    features: [
      "Android Automotive OS (AAOS)",
      "AOSP Customization",
      "Infotainment Platforms (Android, QNX, RTOS)",
      "BSP, Middleware, IPC",
      "Boot Time Optimization & OTA"
    ]
  },
  {
    id: "mobile-engineering",
    title: "Mobile Engineering",
    description: "Native and cross-platform mobile development with enterprise-grade architecture and CI/CD integration.",
    features: [
      "Android & iOS Native Development",
      "Cross-Platform (React Native / Flutter)",
      "Mobile Architecture & Scalability",
      "CI/CD for Mobile Apps",
      "Performance, Security & Release Readiness"
    ]
  },
  {
    id: "architecture-design",
    title: "Architecture & System Design",
    description: "System and software architecture consulting for platform modernization and scalable solutions.",
    features: [
      "System & Software Architecture",
      "High-Level and Low-Level Design",
      "Platform Modernization",
      "Cloud & Edge Integration",
      "Microservices & Embedded Patterns"
    ]
  },
  {
    id: "testing-quality",
    title: "Testing & Quality Engineering",
    description: "Comprehensive quality engineering with automation frameworks and CI-integrated quality gates.",
    features: [
      "Unit, Integration, System Testing",
      "Automation Frameworks",
      "HIL / SIL / MIL Testing",
      "CI-Integrated Quality Gates",
      "Static Analysis & Coverage Improvement"
    ]
  },
  {
    id: "ai-services",
    title: "AI-Enabled Engineering Services",
    description: "Intelligent automation for engineering teams using AI-assisted code review, test generation, and defect prediction.",
    features: [
      "AI-Assisted Code Review",
      "AI-Driven Test Case Generation",
      "Defect Prediction & Trend Analysis",
      "AI for Requirements Analysis",
      "Productivity Automation"
    ]
  },
  {
    id: "rfq-support",
    title: "RFQ / RFP Response Consulting",
    description: "Professional RFQ/RFP response services with technical writing and OEM-style documentation.",
    features: [
      "Technical RFQ Response Writing",
      "Architecture & Solution Sections",
      "Compliance Matrices",
      "Delivery Model & Staffing Plans",
      "Risk & Mitigation Narratives"
    ]
  },
  {
    id: "staffing",
    title: "Staffing & Resource Augmentation",
    description: "Senior technical talent for Automotive, Mobile, and AI domains—ready to integrate with your teams and deliver from day one.",
    features: [
      "Automotive Software Engineers (AAOS, AOSP, QNX)",
      "Mobile Developers (Android, iOS, React Native)",
      "AI/ML Engineers & Data Scientists",
      "Program & Project Managers (SAFe Certified)",
      "ASPICE & Quality Consultants"
    ]
  }
];

export const industries = [
  {
    name: "Automotive",
    description: "OEMs, Tier-1 suppliers, and automotive technology companies transforming vehicle software.",
    focus: ["SDV Development", "ADAS Systems", "Infotainment", "Connected Vehicles"]
  },
  {
    name: "Mobile",
    description: "Enterprise mobile applications and consumer-facing products requiring scale and performance.",
    focus: ["Enterprise Apps", "Consumer Products", "IoT Integration", "Mobile Security"]
  },
  {
    name: "AI & Technology",
    description: "Organizations leveraging AI to accelerate engineering productivity and innovation.",
    focus: ["AI Integration", "ML Pipelines", "Intelligent Automation", "Data Engineering"]
  }
];

export const frameworks = [
  {
    name: "SAFe / Agile",
    description: "Scaled Agile Framework implementation for large automotive programs with multi-team coordination."
  },
  {
    name: "Automotive SPICE",
    description: "ASPICE assessment and process improvement from capability level 1 through level 3."
  },
  {
    name: "SDLC Best Practices",
    description: "Industry-standard software development lifecycle with requirements traceability and quality gates."
  },
  {
    name: "CI/CD & DevOps",
    description: "Continuous integration, delivery, and deployment pipelines for automotive and mobile software."
  },
  {
    name: "Secure Coding",
    description: "Security-first development practices with static analysis and vulnerability management."
  },
  {
    name: "AI-Assisted SDLC",
    description: "Integration of AI tools across the development lifecycle for enhanced productivity."
  }
];

export const whyUsReasons = [
  {
    title: "Experienced Core + Accelerated Enablement",
    description: "We staff your engagement with experienced engineers and leads, each supported by senior specialists with 10+ years of expertise. For areas requiring precise alignment to your environment or toolchain, we run a short accelerated enablement track so resources are productive quickly while still maintaining quality expectations."
  },
  {
    title: "OEM Battle-Tested",
    description: "Our processes have been validated across global OEMs and Tier-1 suppliers in production environments."
  },
  {
    title: "Execution Over Slides",
    description: "We deliver working code, functioning processes, and measurable outcomes—not PowerPoint strategies."
  },
  {
    title: "Domain Depth",
    description: "Deep expertise in automotive software, mobile platforms, and AI—not generic IT consulting."
  },
  {
    title: "Transparent Governance",
    description: "Real-time visibility into project health, risks, and deliverables through structured reporting."
  },
  {
    title: "Flexible Engagement",
    description: "From advisory consulting to full delivery, we adapt our model to your organizational needs."
  }
];

export const contactInfo = {
  title: "Let's Build Together",
  subtitle: "Ready to accelerate your automotive, mobile, or AI initiatives? Connect with our expert team.",
  email: "hello@vc4u.co.in",
  cta: "Schedule a Consultation"
};

export const footerContent = {
  copyright: `© ${new Date().getFullYear()} VC4U (Vyuham Consulting for You). All rights reserved.`,
  tagline: "Consult, Code, Collaborate",
  links: [
    { label: "Privacy Policy", href: "#" },
    { label: "Terms of Service", href: "#" }
  ]
};

// SEO Meta Data
export const seoMeta = {
  title: "VC4U | Automotive Software, Mobile & AI Consulting",
  description: "Senior-led IT consulting for automotive OEMs, Tier-1 suppliers, and enterprise technology leaders. ASPICE, SAFe, AAOS, and AI-enabled engineering services.",
  keywords: [
    "Automotive software consulting",
    "Android Automotive consulting",
    "ASPICE consulting services",
    "SAFe consulting for automotive",
    "Automotive program management",
    "Mobile application consulting",
    "AI in software engineering",
    "RFQ response consulting",
    "Software architecture consulting"
  ]
};
